import { CircularProgress, styled, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { ComponentProps, FC } from 'react';
import { useResponsiveText } from '../../hooks';
import { CircularProgressIndicatorProps } from './circular-progress-indicator.types';

interface CircularProgressWithLabelProps extends Omit<ComponentProps<typeof CircularProgress>, 'value'> {
  customColor: string;
  loadingHint: string;
  value: number;
}

interface StyledCircularProgressProps {
  customColor: string;
}

const StyledCircularProgress = styled(CircularProgress)<StyledCircularProgressProps>(({ customColor }) => ({
  color: customColor,
}));

export const CircularProgressWithLabel: FC<CircularProgressWithLabelProps> = ({ customColor, loadingHint, value, ...rest }) => {
  const captionStyles = useResponsiveText('0.75rem', 4, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 5,
  });

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <StyledCircularProgress
        {...rest}
        value={value}
        variant="determinate"
        customColor={customColor}
      />
      <Box
        sx={{
          alignItems: 'center',
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          left: 0,
          position: 'absolute',
          right: 0,
          top: 0,
        }}
      >
        <Tooltip title={loadingHint} arrow>
          <Typography
            color="text.secondary"
            component="div"
            variant="caption"
            style={{
              fontSize: captionStyles.fontSize,
              lineHeight: captionStyles.lineHeight,
              wordBreak: captionStyles.wordBreak,
              whiteSpace: captionStyles.whiteSpace,
              overflow: captionStyles.overflow,
              textOverflow: captionStyles.textOverflow,
            }}
          >
            {`${Math.round(value)}%`}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

const CircularProgressIndicator: FC<CircularProgressIndicatorProps> = ({
  color,
  isLoadingOthers,
  loadingHint,
  retrievedCount,
  totalCount,
}) => {
  const progress = (): number => {
    if (!totalCount && !retrievedCount) return 0;
    if (retrievedCount !== totalCount) return (retrievedCount / totalCount) * 100;
    if (isLoadingOthers) return 99;
    return 100;
  };
  return <CircularProgressWithLabel customColor={color} loadingHint={loadingHint} value={progress()} />;
};

export default CircularProgressIndicator;
